/* eslint-disable no-unused-vars */
import { requestLogin } from '../../services/request-login';
import Wix from '../../services/wix-sdk-polyfill';
import * as communityActions from '../community/actions';
import { renewFFMembers } from '../ff-page/actions';
import { FOLLOWING_FOLLOWERS } from '../../constants/app-components';
import { getAppComponent, getSignedInstance, getIsDemoMode } from '../site/selectors';
import { getViewedMember, findMemberByUid, getCurrentMember, isMemberInCommunity } from './selectors';
import { sendActionsToFrames, sendActionToFrames } from '../../services/frames-messages';
import { CHAT_APP_DEF_ID } from '../../constants/apps';
import BlockMemberService from '../../services/member-block-member';
import ReportMemberService from '../../services/member-report';

export const FOLLOW_IN_STATE = 'members/FOLLOW_IN_STATE';
export const UNFOLLOW_IN_STATE = 'members/UNFOLLOW_IN_STATE';
export const SET_MEMBERS = 'members/SET_MEMBERS';
export const SET_CURRENT_MEMBER = 'members/SET_CURRENT_MEMBER';
export const SET_VIEWED_MEMBER = 'members/SET_VIEWED_MEMBER';
export const SET_VIEWED_MEMBER_LOADING = 'members/SET_VIEWED_MEMBER_LOADING';
export const OPEN_MEMBERS_CHAT = 'members/OPENT_MEMBERS_CHAT';
export const MEMBERS_LIST_LAYOUT_CHANGE = 'members/MEMBERS_LIST_LAYOUT_CHANGE';
export const BI_FOLLOW_UNFOLLOW = 'bi/FOLLOW_UNFOLLOW';

export const memberListLayoutChange = (layoutType) => ({
  type: MEMBERS_LIST_LAYOUT_CHANGE,
  layoutType,
});
export const setCurrentMember = (member) => ({
  type: SET_CURRENT_MEMBER,
  payload: member,
});
export const setViewedMember = (member) => ({
  type: SET_VIEWED_MEMBER,
  payload: member,
});
export const setViewedMemberLoading = (loading) => ({
  type: SET_VIEWED_MEMBER_LOADING,
  payload: loading,
});
export const setMembers = (members) => ({ type: SET_MEMBERS, payload: members });
export const setMember = (member) => setMembers([member]);
export const followInState = (follower, followed) => ({
  type: FOLLOW_IN_STATE,
  payload: { follower, followed },
});
export const unfollowInState = (unfollower, unfollowed) => ({
  type: UNFOLLOW_IN_STATE,
  payload: { unfollower, unfollowed },
});
export const fetchMember = (memberId) => (dispatch, getState, { request }) => request(`/_api/members/${memberId}`);
export const biFollowUnfollow = (isFollowed, actingOn) => ({
  type: BI_FOLLOW_UNFOLLOW,
  isFollowed,
  actingOn,
});

export const openMembersChat = (messagedMemberId) => ({
  type: OPEN_MEMBERS_CHAT,
  messagedMemberId,
});

export const initCurrentMember = ({ currentMember }) => (dispatch) => {
  if (currentMember) {
    dispatch(setCurrentMember(currentMember));
  }
};

export const initViewedMember = ({ viewedMember }) => (dispatch) => {
  if (viewedMember) {
    dispatch(setViewedMember(viewedMember));
  }
};

export const renewViewedMember = (newUid) => async (dispatch, getState) => {
  const state = getState();
  const viewedMember = getViewedMember(state);
  const uid = newUid || viewedMember.uid;
  const member = await dispatch(fetchMember(uid));
  const isFF = getAppComponent(state) === FOLLOWING_FOLLOWERS;

  dispatch(setViewedMember(member));
  sendActionToFrames(setViewedMember(member));

  if (isFF) {
    dispatch(renewFFMembers());
  }
};

export const fetchAndSetViewedMember = (uid) => async (dispatch) => {
  dispatch(setViewedMemberLoading(true));
  sendActionToFrames(setViewedMemberLoading(true));
  await dispatch(renewViewedMember(uid));
  dispatch(setViewedMemberLoading(false));
  sendActionToFrames(setViewedMemberLoading(false));
};

export const refreshCurrentMember = () => () => {
  Wix.refreshCurrentMember();
};

export const updateMember = (memberId, data) => (dispatch, getState, { request }) => {
  return request
    .patch(`/_api/members/${memberId}`, data)
    .then((updatedMember) => sendActionToFrames(setMember(updatedMember)))
    .then(() => dispatch(refreshCurrentMember()));
};

export const followOrUnfollow = (uid) => (dispatch, getState, { request }) => {
  const state = getState();
  const member = findMemberByUid(state, uid);
  const currentMember = getCurrentMember(state);

  if (getIsDemoMode(state)) {
    return;
  }

  if (!currentMember) {
    requestLogin();
    return Promise.resolve();
  }

  if (!isMemberInCommunity(currentMember)) {
    return dispatch(communityActions.openJoinCommunityModal());
  }

  const action = member.isSubscribed ? unfollowInState(currentMember, member) : followInState(currentMember, member);
  const suffix = member.isSubscribed ? 'unfollow' : 'follow';

  dispatch(biFollowUnfollow(!member.isSubscribed, member.uid));
  sendActionsToFrames([setMember(member), action]);

  return request.post(`/_api/members/${member.uid}/${suffix}`);
};

export const followOrUnfollowViewedMember = () => (dispatch, getState) => {
  const member = getViewedMember(getState());
  return dispatch(followOrUnfollow(member.uid));
};

export const removeMember = (uid) => (dispatch, getState, { request }) => request.delete(`/_api/members/${uid}`);

export const openChat = (memberId) => (dispatch, getState, { request }) => {
  const state = getState();
  const currentMember = getCurrentMember(state);

  if (!currentMember) {
    requestLogin();
    return Promise.resolve();
  }

  if (!isMemberInCommunity(currentMember)) {
    return dispatch(communityActions.openJoinCommunityModal());
  }

  dispatch(openMembersChat(memberId));
  Wix.SuperApps.getPublicAPI({ appDefinitionId: CHAT_APP_DEF_ID }, (api) => api.startPrivateChat(memberId));
};

export const blockMember = (memberId) => (dispatch, getState) => {
  const state = getState();
  const blockMemberService = new BlockMemberService(() => getSignedInstance(state));
  return blockMemberService.blockMember(memberId);
};

export const reportMember = (reportedMemberId, reportingMemberId, reason) => (dispatch, getState) => {
  const state = getState();
  const reportMemberService = new ReportMemberService(() => getSignedInstance(state));
  return reportMemberService.reportMember(reportedMemberId, reportingMemberId, reason);
};
